<template>
  <!--
    ToVid FeedbackHost · toast 区：fixed 顶 24、居中、gap-2、z=90
    ToastView：h-10 max-w-480 rounded-md bg-surface-3 + ToneIcon
  -->
  <Teleport to="body">
    <div class="au-toast-host" aria-live="polite">
      <div v-for="item in toasts" :key="item.id" class="au-toast-enter">
        <div role="status" class="au-toast">
          <component :is="toneIcon(item.tone)" class="au-toast-icon" :class="`is-${item.tone}`" :size="16" />
          <span class="au-toast-text">{{ item.text }}</span>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
/**
 * AuroraToastHost — 全局挂载一次，渲染 auroraToast 队列
 */
import { onBeforeUnmount, onMounted, ref, type Component } from "vue";
import { AlertTriangle, CheckCircle2, Info, Loader2, XCircle } from "@lucide/vue";
import {
  getAuroraToasts,
  subscribeAuroraToast,
  type AuroraToastItem,
  type AuroraToastTone,
} from "@/utils/auroraToast";

const toasts = ref<AuroraToastItem[]>([]);
let unsubscribe: (() => void) | null = null;

function toneIcon(tone: AuroraToastTone): Component {
  if (tone === "success") return CheckCircle2;
  if (tone === "warning") return AlertTriangle;
  if (tone === "error") return XCircle;
  if (tone === "loading") return Loader2;
  return Info;
}

function sync(): void {
  toasts.value = [...getAuroraToasts()];
}

onMounted(() => {
  sync();
  unsubscribe = subscribeAuroraToast(sync);
});

onBeforeUnmount(() => {
  unsubscribe?.();
  unsubscribe = null;
});
</script>

<style scoped lang="scss">
.au-toast-host {
  pointer-events: none;
  position: fixed;
  /* ToVid FEEDBACK.z.toast=90；本站弹层更高，抬到抽屉之上保证可见 */
  z-index: 10060;
  inset-inline: 0;
  top: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.au-toast-enter {
  pointer-events: auto;
  animation: au-toast-fade-down 240ms cubic-bezier(0.25, 0.8, 0.25, 1) both;
}

.au-toast {
  display: flex;
  height: 40px;
  max-width: 480px;
  align-items: center;
  gap: 8px;
  border-radius: 12px;
  background: var(--ch-surface-3, #e6e6ec);
  padding: 0 14px;
  color: var(--ch-fg, #0e0e11);
  font-size: 14px;
  white-space: nowrap;
  box-shadow: var(--ch-shadow-4, 0 24px 64px -12px rgba(14, 14, 17, 0.22));
}

:global(html.dark) .au-toast,
:global(html[theme="dark"]) .au-toast,
:global(html[data-theme="dark"]) .au-toast {
  --ch-surface-3: #1f1f30;
  --ch-fg: #fff;
  --ch-success: #2fc77e;
  --ch-accent: #8261ff;
  --ch-warning: #ffb547;
  --ch-danger: #ff4d5e;
  background: var(--ch-surface-3);
  color: var(--ch-fg);
  box-shadow: 0 24px 64px -12px rgba(0, 0, 0, 0.55);
}

.au-toast-icon {
  flex-shrink: 0;
}

.au-toast-icon.is-success {
  color: var(--ch-success, #1fae69);
}

.au-toast-icon.is-info {
  color: var(--ch-accent, #7650ff);
}

.au-toast-icon.is-warning {
  color: var(--ch-warning, #e8960f);
}

.au-toast-icon.is-error {
  color: var(--ch-danger, #ef3347);
}

.au-toast-icon.is-loading {
  color: var(--ch-accent, #7650ff);
  animation: au-toast-spin 0.8s linear infinite;
}

.au-toast-text {
  overflow: hidden;
  text-overflow: ellipsis;
}

@keyframes au-toast-fade-down {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes au-toast-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .au-toast-enter {
    animation: none;
  }
  .au-toast-icon.is-loading {
    animation: none;
  }
}
</style>
